import { useQuery } from '@apollo/client';
import sortBy from 'lodash-es/sortBy';
import { useHistory } from 'react-router-dom';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GET_CLINIC_WORKFLOWS_EVENT_SETTINGS } from 'shared/queries/workflows';
import {
  ClinicOfficeHourSelectionFragment,
  ClinicUpdateInput,
  useUpdateClinicSettingsClinicMutation,
  ClinicOfficeHoursType,
  WorkflowEventSetting,
  useGetClinicSettingsGeneralQuery,
  WorkflowEvent,
} from 'shared/types/graphql';
import AutomationTable from './AutomationTable';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import OfficeHoursPicker, { CanceledButtonClicked } from '../GeneralInfoSettings/OfficeHoursPicker';
import { ClinicHoursUpdateField } from '../ClinicHoursSettings';
import { parseISO } from 'date-fns';
import { Box, Heading, Divider, Text, ListItem, UnorderedList } from '@televet/kibble-ui';
const AutomationsSettings = forwardRef(
  (
    {
      hasPermission,
      setHasUnsavedChange,
    }: {
      hasPermission?: boolean;
      setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref,
  ): JSX.Element => {
    const { currentClinicId } = useClinicUser();
    const { toastMessagePop } = useCustomToast();
    const history = useHistory();
    const childRefTwo = useRef<CanceledButtonClicked>(null);
    const [automationHourChanges, setAutomationHourChanges] = useState<ClinicHoursUpdateField[]>();
    const [newAutomationHours, setNewAutomationHours] = useState<ClinicOfficeHourSelectionFragment[]>([]);
    const [automationHour, setAutomationHour] = useState<ClinicOfficeHourSelectionFragment[]>([]);

    const [updateClinicSettings] = useUpdateClinicSettingsClinicMutation();

    const { data: currentClinic, refetch: refetchGeneralSettingsData } = useGetClinicSettingsGeneralQuery({
      variables: { id: currentClinicId || '' },
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
      skip: !currentClinicId,
    });

    const { data: workflowsData, loading: workflowEventSettingsLoading } = useQuery(
      GET_CLINIC_WORKFLOWS_EVENT_SETTINGS,
      {
        variables: {
          where: {
            clinic: { id: { equals: currentClinicId } },
            event: { not: { equals: WorkflowEvent.ServiceReminderFormSubmitted } },
          },
        },
        fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
      },
    );

    const filteredWorkflowEventSettings = useMemo(
      () =>
        workflowsData?.findManyWorkflowEventSetting.filter(
          (workflow: WorkflowEventSetting) => !workflow.surveySettingId,
        ),
      [workflowsData?.findManyWorkflowEventSetting],
    );

    const isAutomationHoursSet = useMemo(() => {
      const clinicOfficeHours = currentClinic?.findUniqueClinic?.clinicOfficeHours || [];
      return clinicOfficeHours.some(({ type }) => type === ClinicOfficeHoursType.AutomationHours);
    }, [currentClinic]);

    const clinicData = currentClinic?.findUniqueClinic;

    const setInitialData = useCallback(() => {
      if (clinicData) {
        const officeHours = clinicData.clinicOfficeHours.map((officeHour: ClinicOfficeHourSelectionFragment) => {
          return {
            ...officeHour,
            openAt: parseISO(officeHour.openAt),
            closeAt: parseISO(officeHour.closeAt),
          };
        });
        if (officeHours.length) {
          setAutomationHour([...officeHours]);
        }
      }
    }, [clinicData]);

    useEffect(() => {
      setInitialData();
    }, [setInitialData]);

    const clinicTimezone = currentClinic?.findUniqueClinic?.timezoneName || '';

    const onAutomationHourPickerChange = (
      hasOfficeHourChanged: boolean,
      newAutomationHours?: ClinicOfficeHourSelectionFragment[],
    ): void => {
      if (hasOfficeHourChanged && !!newAutomationHours) {
        setHasUnsavedChange(true);
        setAutomationHourChanges((prevVal) => {
          const newVal = prevVal || [];
          if (newVal.includes(ClinicHoursUpdateField.OfficeHoursState) === false) {
            return [...newVal, ClinicHoursUpdateField.OfficeHoursState];
          }
          return newVal;
        });
        setNewAutomationHours(newAutomationHours);
      } else {
        const newState = automationHourChanges?.filter(
          (clinicHourUpdateField) => clinicHourUpdateField !== ClinicHoursUpdateField.OfficeHoursState,
        );
        setAutomationHourChanges(newState || []);
        if (!!newState) {
          setHasUnsavedChange(false);
        }
      }
    };

    const onSaveClinicHoursChange = (): void => {
      if (automationHourChanges?.includes(ClinicHoursUpdateField.OfficeHoursState)) {
        const updatedOfficeHours = newAutomationHours.map((officeHour: ClinicOfficeHourSelectionFragment) => {
          const { id, openAt, closeAt, isEnabled, isOpenTwentyFourHours } = officeHour;

          return {
            where: { id },
            data: {
              openAt,
              closeAt,
              isEnabled,
              isOpenTwentyFourHours,
            },
          };
        });
        onUpdate({ clinicOfficeHours: { update: updatedOfficeHours } });
      }
    };

    const onUpdate = useCallback(
      async (data: ClinicUpdateInput) => {
        if (currentClinicId) {
          try {
            await updateClinicSettings({
              variables: { data, id: currentClinicId },
            });

            toastMessagePop();
            await refetchGeneralSettingsData();
          } catch (e) {
            console.error(e);
            toastMessagePop('Your clinic was unable to be updated. Please try again.', STATUSES.error);
          }
        }
      },
      [currentClinicId, updateClinicSettings, toastMessagePop, refetchGeneralSettingsData],
    );

    const onChangesSaveCancelled = (): void => {
      childRefTwo.current?.onResetOfficeHours();
    };

    useImperativeHandle(ref, () => ({
      onLeavingCurrentPage(): void {
        setAutomationHourChanges([]);
      },
      onClickedSaveButton(): void {
        onSaveClinicHoursChange();
      },
      onClickedCancelButton(): void {
        onChangesSaveCancelled();
      },
    }));
    return (
      <Box alignItems="flex-start" minW="1060px">
        <Box py={4}>
          <Heading size="sm" id="automation-hours" pb={3}>
            Sending Hours
          </Heading>
          <UnorderedList>
            <ListItem>
              <Text size="sm">
                Please adjust accordingly if you have appointments that require a reminder during your closed hours.
              </Text>
            </ListItem>
            <ListItem>
              <Text size="sm" fontWeight="bold">
                Automation Hours
              </Text>
              <Text size="sm"> - Will only send during the automation hours selected in this page.</Text>
            </ListItem>
            <ListItem>
              <Text size="sm" fontWeight="bold">
                Clinic Hours
              </Text>
              <Text size="sm">
                - Will only send during your clinic hours selected in
                <Text
                  size="sm"
                  variant="interactive"
                  cursor="pointer"
                  onClick={(): void => {
                    history.push('/settings/clinic/hours');
                  }}
                >
                  {' '}
                  Clinic Hours
                </Text>
                .
              </Text>
            </ListItem>
            <ListItem>
              <Text size="sm" fontWeight="bold">
                24 Hours a Day
              </Text>
              <Text size="sm"> - Will send at any hour of the day based on when the automation is set to start.</Text>
            </ListItem>
          </UnorderedList>
        </Box>
        <Divider />
        <Box py={6}>
          <Heading size="sm" id="automation-hours" pb={3}>
            Automation Hours
          </Heading>
          <OfficeHoursPicker
            hasPermission={hasPermission}
            clinicOfficeHoursType={ClinicOfficeHoursType.AutomationHours}
            clinicHoursData={automationHour}
            clinicTimezone={clinicTimezone}
            toastMessagePop={toastMessagePop}
            ref={childRefTwo}
            setClinicHoursChanges={(
              hasOfficeHourChanged: boolean,
              newAutomationHours?: ClinicOfficeHourSelectionFragment[],
            ): void => onAutomationHourPickerChange(hasOfficeHourChanged, newAutomationHours)}
          />
        </Box>
        <Divider />
        <AutomationTable
          hasPermission={hasPermission}
          workflowEventSettings={sortBy(
            filteredWorkflowEventSettings || [],
            (workflowEventSetting) => workflowEventSetting.name,
          )}
          workflowEventSettingsLoading={workflowEventSettingsLoading}
          isAutomationHoursSet={isAutomationHoursSet}
        />
      </Box>
    );
  },
);

AutomationsSettings.displayName = 'AutomationsSetting';
export default AutomationsSettings;
